import React from 'react';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import { Text } from '~/components/core';
import AlertBanner from '~/components/core/AlertBanner';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Link from '~/components/core/Atomic/Link/Link';
import MenuItem from '~/components/core/Atomic/MenuItem';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import TextField from '~/components/core/Molecules/Fields/TextField';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import SortableTable from '~/components/core/Tables/SortableTable';
import { useCms } from '~/components/hooks/useCms';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import CancelIcon from '~/components/icons/CancelIcon';
import useOrganization from '~/components/OrganizationContext';
import Radio from '~/components/ui/atoms/Radio';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  FnolInboxAvailableInboxesForGqMoveResponse,
  FnolInboxAvailableInboxesResponse,
  FnolInboxAvailableInboxesResponseFnolInboxConfigOption,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';
import cn from '~/Utils/cn';
import { getLobDescription } from '~/Utils/lobUtils';

export type InboxConfigOption = FnolInboxAvailableInboxesResponseFnolInboxConfigOption;

interface InboxSelectionDialogProps {
  title: string;
  description: string;
  fetchUrl: string;
  onSave: (selectedInbox: InboxConfigOption) => Promise<void>;
  onClose: () => void;
  displayOnMove?: boolean;
  onMoveWithoutSelection?: () => Promise<void> | void;
}

const ALL_OPTION = 'all';
const ROWS_PER_PAGE = 5;

export const InboxSelectionDialog: React.FC<InboxSelectionDialogProps> = ({
  title,
  description,
  fetchUrl,
  onSave,
  onClose,
  displayOnMove = false,
  onMoveWithoutSelection,
}) => {
  const { classes } = useStyles();
  const { userOrganization } = useCms();
  const { lobConfigurationsDict } = useLobConfiguration();
  const showSubOrgColumn = userOrganization.sub_organizations_enabled;
  const { subOrganizations, emailDomains } = useOrganization() as {
    subOrganizations: { id: number; email_domain_id?: number }[];
    emailDomains: { id: number; domain: string; sub_organization_ids: number[] }[];
  };

  const { data, isLoading, isError } = useDataFetcher<
    FnolInboxAvailableInboxesResponse | FnolInboxAvailableInboxesForGqMoveResponse
  >(fetchUrl);
  const recommendedInbox = (data && 'recommended_inbox' in data ? data.recommended_inbox : null) ?? null;

  const [selectedInbox, setSelectedInbox] = React.useState<InboxConfigOption | null>(null);
  const [searchText, setSearchText] = React.useState('');
  const [subOrgFilter, setSubOrgFilter] = React.useState<string>(ALL_OPTION);
  const [lobFilter, setLobFilter] = React.useState<string>(ALL_OPTION);
  const [isSaving, setIsSaving] = React.useState(false);
  const [page, setPage] = React.useState(0);

  const availableInboxes = React.useMemo(() => data?.available_inboxes ?? [], [data]);

  // Auto-select the recommended inbox when data loads
  React.useEffect(() => {
    if (recommendedInbox && availableInboxes.length > 0 && !selectedInbox) {
      const match = availableInboxes.find(
        (inbox) =>
          inbox.sub_organization_id === recommendedInbox.sub_organization_id && inbox.lob === recommendedInbox.lob
      );
      if (match) setSelectedInbox(match);
    }
  }, [recommendedInbox, availableInboxes]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDeviatingFromRecommendation = React.useMemo(() => {
    if (!recommendedInbox || !selectedInbox) return false;
    return (
      selectedInbox.sub_organization_id !== recommendedInbox.sub_organization_id ||
      selectedInbox.lob !== recommendedInbox.lob
    );
  }, [recommendedInbox, selectedInbox]);

  const getEmailDomain = React.useCallback(
    (subOrgId: number | null): string | undefined => {
      if (!subOrgId) {
        return emailDomains.find((d: { sub_organization_ids?: number[] }) => !d.sub_organization_ids?.length)?.domain;
      }
      const subOrg = subOrganizations.find((so: { id: number }) => so.id === subOrgId);
      if (subOrg?.email_domain_id) {
        const domainObj = emailDomains.find((d: { id: number }) => d.id === subOrg.email_domain_id);
        if (domainObj) return domainObj.domain;
      }
      return emailDomains.find((d: { sub_organization_ids?: number[] }) => !d.sub_organization_ids?.length)?.domain;
    },
    [subOrganizations, emailDomains]
  );

  const getFullInboxEmail = React.useCallback(
    (inbox: InboxConfigOption): string => {
      const domain = getEmailDomain(inbox.sub_organization_id ?? null);
      return domain ? `${inbox.fnol_email_inbox_name}@${domain}` : inbox.fnol_email_inbox_name;
    },
    [getEmailDomain]
  );

  const subOrgOptions = React.useMemo(() => {
    const uniqueSubOrgs = new Map<string, string>();
    for (const inbox of availableInboxes) {
      const key = String(inbox.sub_organization_id ?? 'none');
      const name = inbox.sub_organization_name ?? 'N/A';
      if (!uniqueSubOrgs.has(key)) {
        uniqueSubOrgs.set(key, name);
      }
    }
    return [
      { label: 'All', value: ALL_OPTION },
      ...Array.from(uniqueSubOrgs.entries()).map(([value, label]) => ({ label, value })),
    ];
  }, [availableInboxes]);

  const lobOptions = React.useMemo(() => {
    const uniqueLobs = new Set<string>();
    for (const inbox of availableInboxes) {
      uniqueLobs.add(inbox.lob);
    }
    return [
      { label: 'All', value: ALL_OPTION },
      ...Array.from(uniqueLobs).map((lob) => ({
        label: getLobDescription(lob, lobConfigurationsDict),
        value: lob,
      })),
    ];
  }, [availableInboxes, lobConfigurationsDict]);

  const filteredInboxes = React.useMemo(() => {
    return availableInboxes.filter((inbox) => {
      if (showSubOrgColumn && subOrgFilter !== ALL_OPTION) {
        const subOrgKey = String(inbox.sub_organization_id ?? 'none');
        if (subOrgKey !== subOrgFilter) return false;
      }
      if (lobFilter !== ALL_OPTION && inbox.lob !== lobFilter) return false;
      if (searchText) {
        const search = searchText.toLowerCase();
        const matchesSubOrg = showSubOrgColumn && inbox.sub_organization_name?.toLowerCase().includes(search);
        const matchesLob = (getLobDescription(inbox.lob, lobConfigurationsDict) ?? inbox.lob)
          .toLowerCase()
          .includes(search);
        const matchesInbox = getFullInboxEmail(inbox).toLowerCase().includes(search);
        if (!matchesSubOrg && !matchesLob && !matchesInbox) return false;
      }
      return true;
    });
  }, [
    availableInboxes,
    showSubOrgColumn,
    subOrgFilter,
    lobFilter,
    searchText,
    lobConfigurationsDict,
    getFullInboxEmail,
  ]);

  const rows = React.useMemo(
    () =>
      filteredInboxes.map((inbox, index) => ({
        id: index,
        ...inbox,
      })),
    [filteredInboxes]
  );

  React.useEffect(() => {
    setPage(0);
  }, [searchText, subOrgFilter, lobFilter]);

  const handleClearFilters = () => {
    setSearchText('');
    setSubOrgFilter(ALL_OPTION);
    setLobFilter(ALL_OPTION);
  };

  const handleSave = async () => {
    if (!selectedInbox) return;
    setIsSaving(true);
    try {
      await onSave(selectedInbox);
      onClose();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSecondary = async () => {
    if (displayOnMove && onMoveWithoutSelection) {
      if (isSaving) return;
      setIsSaving(true);
      try {
        await onMoveWithoutSelection();
        onClose();
      } catch (error) {
        await reportAxiosError(error);
      } finally {
        setIsSaving(false);
      }
    } else {
      onClose();
    }
  };

  const paginatedRows = React.useMemo(() => {
    if (rows.length <= ROWS_PER_PAGE) return rows;
    return rows.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);
  }, [rows, page]);

  const selectedRowIds = React.useMemo(() => {
    if (!selectedInbox) return [];
    return paginatedRows
      .filter((row) => row.sub_organization_id === selectedInbox.sub_organization_id && row.lob === selectedInbox.lob)
      .map((row) => row.id);
  }, [paginatedRows, selectedInbox]);

  const paginationProps = React.useMemo(() => {
    if (rows.length <= ROWS_PER_PAGE) return undefined;
    return {
      rowsPerPageOptions: [ROWS_PER_PAGE],
      count: rows.length,
      rowsPerPage: ROWS_PER_PAGE,
      page,
      onChangePage: (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => setPage(newPage),
      onChangeRowsPerPage: undefined,
    };
  }, [rows.length, page]);

  const columns = [
    {
      id: 'radio',
      label: '',
      numeric: false,
      width: '50px',
      specialCell: (row: InboxConfigOption & { id: number }) => {
        const isSelected =
          selectedInbox?.sub_organization_id === row.sub_organization_id && selectedInbox?.lob === row.lob;
        return <Radio name="selected_inbox" checked={isSelected} onChange={() => setSelectedInbox(row)} />;
      },
    },
    ...(showSubOrgColumn
      ? [
          {
            id: 'sub_organization_name',
            label: 'Sub-Org',
            numeric: false,
            specialCell: (row: InboxConfigOption) => row.sub_organization_name ?? 'N/A',
          },
        ]
      : []),
    {
      id: 'lob',
      label: 'Line of Business',
      numeric: false,
      specialCell: (row: InboxConfigOption) => getLobDescription(row.lob, lobConfigurationsDict),
    },
    {
      id: 'fnol_email_inbox_name',
      label: 'Inbox',
      numeric: false,
      specialCell: (row: InboxConfigOption) => getFullInboxEmail(row),
    },
  ];

  return (
    <CardDialog
      isDialog
      open
      fullWidth
      maxWidth="md"
      onClose={onClose}
      title={title}
      preventClose={isSaving}
      footerActions={
        <DialogFooterActions
          onClickPrimary={handleSave}
          onClickSecondary={handleSecondary}
          {...(displayOnMove ? { secondaryLabel: 'Move without selection' } : {})}
          disabled={isSaving}
          primaryDisabled={!selectedInbox}
        />
      }
    >
      <div className="flex flex-col gap-16">
        <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {description}
        </Text>

        {isDeviatingFromRecommendation ? (
          <AlertBanner
            alertType="warning"
            withIcon
            note={`The policy found may not be valid for the selected inbox. Saving will reprocess the claim under the new line of business ${showSubOrgColumn ? '/ sub-organization' : ''}.`}
          />
        ) : null}

        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <TextField
              className={classes.textFieldRow}
              label="Search"
              value={searchText}
              onChange={(val: string) => setSearchText(val)}
            />
          </Grid>
          {showSubOrgColumn ? (
            <Grid item>
              <TextField
                className={classes.textFieldRow}
                label="Sub-Organization"
                onChange={(val: string) => setSubOrgFilter(val)}
                select
                value={subOrgFilter}
              >
                {subOrgOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          ) : null}
          <Grid item>
            <TextField
              className={classes.textFieldRow}
              label="Line of Business"
              onChange={(val: string) => setLobFilter(val)}
              select
              value={lobFilter}
            >
              {lobOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item>
            <Link
              href=""
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                handleClearFilters();
              }}
              className={cn(classes.alignedContainerForGrid, classes.secondaryLink, 'pt-1.5')}
            >
              <CancelIcon className={cn(classes.filledIcon, classes.leftButtonIcon)} />
              Clear all filters
            </Link>
          </Grid>
        </Grid>

        {isLoading || isError ? (
          <SkeletonTable rowsCount={ROWS_PER_PAGE} columnsCount={showSubOrgColumn ? 4 : 3} isError={isError} />
        ) : null}
        {!isLoading && !isError ? (
          <SortableTable
            columns={columns}
            rows={paginatedRows}
            paginationProps={paginationProps}
            disableSortByUser
            keepRowsOrder
            stickyHeader
            onRowClick={(rowId: number) => {
              const row = paginatedRows.find((r) => r.id === rowId);
              if (row) setSelectedInbox(row);
            }}
            selectedRowsIds={selectedRowIds}
            boldRowsIds={selectedRowIds}
          />
        ) : null}
      </div>
    </CardDialog>
  );
};
